<template>
  <div
    class="service-station-block"
    :class="{
      'has-image': showImage && imageSrc,
      'first': shouldBeFirst,
      'second': shouldBeSecond,
    }"
    @mouseenter="mapView ? $emit('block-mouse-over', serviceStation.id) : null"
    @mouseleave="mapView ? $emit('block-mouse-leave', serviceStation.id) : null"
    :style="{
      order: order,
    }"
  >
    <div
      class="spinner-wrapper"
      v-if="loading"
    >
      <spinner
        size="75"
        :line-size="5"
        :line-fg-color="thirdColor"
      />
    </div>
    <div class="service-station-wrapper">
      <div class="service-station-element">
        <div
          v-if="serviceStation.name"
          class="service-station-name"
          @click="mapView ? openMap({ lat: serviceStation.location.lat, lng: serviceStation.location.lng, partner: serviceStation.id }) : null"
          :style="{
            cursor: mapView ? 'pointer' : 'auto',
          }"
        >
          {{ serviceStation.name }}
        </div>
        <div
          class="service-station-favourite-label"
          v-if="showFavLabel"
        >
          {{ isFavourite ? 'Twój Partner Serwisowy' : '' }}
        </div>
        <div
          class="service-station-favourite-label"
          v-if="showClosestLabel"
        >
          {{ isClosest ? 'Najbliższy Partner Serwisowy' : '' }}
        </div>
        <div
          v-if="serviceStation.streetNumber"
          class="service-station-address"
        >
          {{ serviceStation.streetNumber }}
          <span
            @click="openMap({ lat: serviceStation.location.lat, lng: serviceStation.location.lng, partner: serviceStation.id })"
            class="icon-location-arrow"
            v-tooltip.top="{ content: 'Pokaż na mapie', classes: 'car-block-tooltip' }"
          />
        </div>
        <div
          v-if="serviceStation.postal && serviceStation.city"
          class="service-station-address"
        >
          {{ `${serviceStation.postal} ${serviceStation.city}` }}
        </div>
        <div
          class="service-station-phone"
          v-if="serviceStation.phone1 && showDetailsData"
        >
          <span>
            tel.
          </span>
          <a
            :class="{ 'disabled': !isMobile }"
            class="phone-link"
            :href="isMobile ? `tel:${serviceStation.phone1}` : ''"
          >
            {{ serviceStation.phone1 }}
          </a>
        </div>
        <div
          class="service-station-phone"
          v-if="serviceStation.phone2 && showDetailsData"
        >
          <span>
            tel.
          </span>
          <a
            :class="{ 'disabled': !isMobile }"
            class="phone-link"
            :href="isMobile ? `tel:${serviceStation.phone2}` : ''"
          >
            {{ serviceStation.phone2 }}
          </a>
        </div>
        <div
          class="show-more-details"
          v-if="!showDetailsData"
        >
          <a
            class="show-more-details-link"
            href="#"
            @click.prevent="toggleDetails()"
          >
            pokaż więcej informacji
          </a>
        </div>
      </div>
      <div
        v-if="setFavourite"
        class="set-favourite-partner"
      >
        <button
          v-if="!$route.path.includes('no_auth')"
          @click="isFavourite ? deleteFavouritePartner() : setFavouritePartner()"
          class="set-favourite-partner-button"
          v-tooltip.top="{
            content: isFavourite ? 'Cofnij wybór preferowanego Partnera' : 'Wybierz preferowanego Partnera',
            classes: 'car-block-tooltip',
          }"
        >
          <svg svg-inline="" v-if="isFavourite" role="presentation" focusable="false" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="#BB0A30" d="M22.9 8.9L15 7.8 11.5.7 8.2 7.8.3 8.9 6 14.5l-1.3 7.8 7-3.6 7 3.6-1.3-7.8 5.5-5.6z"/></svg>
          <svg svg-inline="" v-else="" role="presentation" focusable="false" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 1.9l3.2 6.4 7.1 1-5.1 5 1.2 7.1-6.3-3.3-6.3 3.3 1.2-7.1-5.1-5 7.1-1z" stroke="currentColor" fill="none"/></svg>
        </button>
      </div>
      <div
        v-if="showDetailsData"
        class="service-station-element details"
      >
        <div
          class="service-station-open"
          v-if="serviceStation.open && showOpenHours"
        >
          <span>Godziny otwarcia:</span>
        </div>
        <div
          class="service-station-days"
          v-if="serviceStation.open && showOpenHours"
        >
          <table class="station-open-table">
            <tr
              v-for="(hours, days, index) in serviceStation.open"
              :key="index"
            >
              <td class="days">
                {{ days }}:
              </td>
              <td class="hours">
                {{ hours }}
              </td>
            </tr>
          </table>
        </div>
        <div
          class="service-station-email"
          v-if="serviceStation.email"
        >
          <span>email: </span>
          <a
            class="link"
            :href="`mailto:${serviceStation.email}`"
            v-tooltip.top="{ content: serviceStation.email, classes: 'car-block-tooltip' }"
          >
            {{ emailAddress }}
          </a>
        </div>
        <div
          class="service-station-www"
          v-if="serviceStation.www"
        >
          <span>www: </span>
          <a
            class="link"
            :href="`http://${serviceStation.www}`"
            target="_blank"
            v-tooltip.top="{ content: serviceStation.www, classes: 'car-block-tooltip' }"
          >
            {{ siteAddress }}
          </a>
        </div>
        <div
          class="show-more-details"
          v-if="showDetailsData"
        >
          <a
            class="show-more-details-link"
            href="#"
            @click.prevent="toggleDetails()"
          >
            ukryj szczegółowe informacje
          </a>
        </div>
      </div>
      <div
        class="service-station-element slot-container"
        v-if="withSlot"
      >
        <slot />
      </div>
    </div>
    <div
      v-if="showImage && imageSrc"
      class="image-container"
      :style="{ backgroundImage: `url('${imageSrc}')` }"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ajax from '@/helpers/ajax';
import config from '@/config';
import Spinner from 'vue-simple-spinner';

export default {
  components: {
    Spinner,
  },
  props: {
    serviceStation: {
      default: () => {},
      type: Object,
    },
    showDetails: {
      default: true,
      type: Boolean,
    },
    setFavourite: {
      default: false,
      type: Boolean,
    },
    withSlot: {
      default: false,
      type: Boolean,
    },
    showPageLink: {
      default: false,
      type: Boolean,
    },
    mapView: {
      default: false,
      type: Boolean,
    },
    showOpenHours: {
      default: true,
      type: Boolean,
    },
    showFavLabel: {
      default: false,
      type: Boolean,
    },
    showClosestLabel: {
      default: true,
      type: Boolean,
    },
    showImage: {
      default: false,
      type: Boolean,
    },
    setOrder: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      showDetailsData: false,
      loading: false,
      shouldBeFirst: false,
      shouldBeSecond: false,
      mounted: false,
      order: '10',
    };
  },
  computed: {
    ...mapGetters({
      fonts: 'brand/getFonts',
      thirdColor: 'brand/getThirdColor',
      userInfo: 'user/getUserInfo',
      serviceStations: 'serviceStations/getServiceStations',
      closestStation: 'serviceStations/getClosest',
      authStatus: 'auth/getAuthStatus',
    }),
    withLogin() {
      return this.authStatus.loggedIn;
    },
    imageSrc() {
      // eslint-disable-next-line
      const targetStation = this.serviceStations.find(station => this.serviceStation.id == station.id);

      if (targetStation) {
        return targetStation.photo;
      }
      return null;
    },
    isFavourite() {
      // eslint-disable-next-line
      return this.userInfo.partner !== null && this.userInfo.partner.id == this.serviceStation.id;
    },
    isClosest() {
      if (this.closestStation) {
        return this.serviceStation.id === this.closestStation.id;
      }
      return false;
    },
    isMobile() {
      return this.$mq === 'mobile' || this.$mq === 'xs';
    },
    siteAddress() {
      if (this.serviceStation.www.length > 30) {
        return `${this.serviceStation.www.slice(0, 30)}...`;
      }
      return this.serviceStation.www;
    },
    emailAddress() {
      if (this.serviceStation.email.length > 30) {
        return `${this.serviceStation.email.slice(0, 30)}...`;
      }
      return this.serviceStation.email;
    },
  },
  methods: {
    ...mapActions({
      setUserInfo: 'user/setUserInfo',
      closeModal: 'modal/closeModal',
    }),
    setFavouritePartner() {
      const endpoint = config.apiEndpoints.favouritePartner.replace('id', this.serviceStation.id);
      this.loading = true;
      ajax.post(endpoint)
        .then((res) => {
          if (res.status === 200) {
            this.setUserInfo();

            this.$emit('set_favourite');

            if (res.status === 403 && res.message === 'Nie masz wystarczających uprawnień do wyświetlenia zasobu') {
              this.$router.push({ path: '/' });
            }

            this.loading = false;
          }
        })
        .catch((error) => {
          console.error(error);

          setTimeout(() => {
            this.loading = false;
          }, 500);
        });
    },
    deleteFavouritePartner() {
      this.loading = true;
      ajax.delete(config.apiEndpoints.deletePartner)
        .then((res) => {
          if (res.status === 200) {
            this.setUserInfo();
          }

          if (res.status === 403 && res.message === 'Nie masz wystarczających uprawnień do wyświetlenia zasobu') {
            this.$router.push({ path: '/' });
          }

          this.loading = false;
        })
        .catch((error) => {
          console.error(error);

          setTimeout(() => {
            this.loading = false;
          }, 500);
        });
    },
    openMap(query) {
      this.closeModal();

      let route = null;

      if (this.withLogin) {
        route = this.$router.resolve({ path: '/dashboard/map', query });
      } else {
        route = this.$router.resolve({ path: '/no_auth/map', query });
      }

      window.open(route.href, '_blank');
    },
    toggleDetails() {
      this.showDetailsData = !this.showDetailsData;
    },
    settingOrder() {
      if (this.isFavourite) {
        this.order = '0';
      } else if (this.isClosest) {
        this.order = '1';
      }
      return '10';
    },
  },
  watch: {
    mounted(newVal) {
      if (newVal && this.setOrder) {
        this.settingOrder();
      }
    },
  },
  mounted() {
    this.showDetailsData = this.showDetails;
    this.mounted = true;
  },
};
</script>
